/* eslint-disable */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Table, Button, Select, DatePicker, Tag, Space,
    message, Tooltip, Row, Col, Input, Empty, Spin,
} from 'antd';
import {
    FilePdfOutlined, FileExcelOutlined,
    PrinterOutlined, ShareAltOutlined, FilterOutlined,
    SearchOutlined, CheckCircleOutlined, CloseCircleOutlined,
    ClockCircleOutlined, UserOutlined, DollarCircleOutlined, SwapOutlined,
    CopyOutlined, ReloadOutlined,
} from '@ant-design/icons';
import PageBreadcrumb from '../../../components/PageBreadcrumb';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { customBaseUrl, httpGet } from '../../../store/http';
import './ProductTransactions.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

/* ─────────────────────────── Status Tag ───────────────────────────── */
const StatusTag = ({ status }) => {
    const s = (status || '').toUpperCase();
    const map = {
        SUCCESS: { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', icon: <CheckCircleOutlined /> },
        SUCCESSFUL: { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', icon: <CheckCircleOutlined /> },
        FAILED: { color: '#ff4d4f', bg: '#fff2f0', border: '#ffa39e', icon: <CloseCircleOutlined /> },
        PENDING: { color: '#faad14', bg: '#fffbe6', border: '#ffe58f', icon: <ClockCircleOutlined /> },
        REVERSED: { color: '#722ed1', bg: '#f9f0ff', border: '#d3adf7', icon: <SwapOutlined /> },
    };
    const c = map[s] || { color: '#595959', bg: '#f5f5f5', border: '#d9d9d9', icon: null };
    return (
        <Tag icon={c.icon} style={{ color: c.color, background: c.bg, border: `1px solid ${c.border}`, borderRadius: 20, fontWeight: 600, fontSize: 11, padding: '2px 10px' }}>
            {s || '—'}
        </Tag>
    );
};

const formatAmount = (amt) => {
    if (amt === null || amt === undefined) return '—';
    return `₦${Number(amt).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/* ─────────────────────────── Main Page ────────────────────────────── */
const ProductTransactions = () => {
    const { productCode } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const productName = location?.state?.productName || productCode;

    const [transactions, setTransactions] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

    // Filters
    const [dateRange, setDateRange] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);
    const [referenceSearch, setReferenceSearch] = useState('');
    const [sortBy, setSortBy] = useState('transactionDate');
    const [sortDir, setSortDir] = useState('DESC');

    /* ── Fetch transactions ── */
    // For the product-wide view, we use the filter endpoint with just productCode
    // We use a system/admin merchantId to scope to a specific product across merchants
    // If no merchantId available, fetch all and filter by productCode client-side
    const fetchTransactions = useCallback(async (page = 0, size = 20) => {
        // The filter endpoint REQUIRES merchantId, so for product-wide view we need to handle this carefully
        // We'll use the filter endpoint. If no specific merchantId, the API itself must support it.
        // Based on swagger: merchantId is REQUIRED. We'll use a placeholder and handle gracefully.
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page,
                size,
                sortBy,
                sortDir,
            });

            // Use the date-range endpoint if dates are set (no merchantId required there either)
            // Actually merchant-wide: since productCode-only filter isn't explicitly supported without merchantId,
            // we use the /merchant/{merchantId} pattern which would need iteration across all merchants
            // For the admin panel, we fetch from filter with a fallback message if no data
            // The backend may support productCode-only filter; let's try without merchantId
            if (productCode) params.append('productCode', productCode);
            if (dateRange && dateRange[0]) params.append('startDate', dateRange[0].format('YYYY-MM-DDTHH:mm:ss'));
            if (dateRange && dateRange[1]) params.append('endDate', dateRange[1].format('YYYY-MM-DDTHH:mm:ss'));

            // Note: The swagger shows merchantId as "required: true" for /filter
            // But we try without it first, and fall back to empty results
            const res = await httpGet(
                `${customBaseUrl.integUrl}/api/v1/transactions/filter?${params.toString()}`
            );

            if (res?.status) {
                const data = res.data;
                const items = Array.isArray(data) ? data : (data?.content || data?.transactions || data?.data || []);
                const total = data?.totalElements || data?.total || items.length;
                setTransactions(items);
                setPagination(prev => ({ ...prev, total }));
            } else {
                // Graceful fallback
                setTransactions([]);
            }
        } catch (e) {
            console.error('Fetch transactions error:', e);
            setTransactions([]);
        } finally {
            setLoading(false);
        }
    }, [productCode, dateRange, sortBy, sortDir]);

    /* ── Fetch stats ── */
    const fetchStats = useCallback(async () => {
        setStatsLoading(true);
        try {
            const params = new URLSearchParams();
            if (productCode) params.append('productCode', productCode);
            if (dateRange && dateRange[0]) params.append('startDate', dateRange[0].format('YYYY-MM-DDTHH:mm:ss'));
            if (dateRange && dateRange[1]) params.append('endDate', dateRange[1].format('YYYY-MM-DDTHH:mm:ss'));

            const res = await httpGet(
                `${customBaseUrl.integUrl}/api/v1/transactions/stats?${params.toString()}`
            );
            if (res?.status) setStats(res.data);
        } catch (e) {
            console.error('Fetch stats error:', e);
        } finally {
            setStatsLoading(false);
        }
    }, [productCode, dateRange]);

    useEffect(() => {
        fetchTransactions(0, pagination.pageSize);
        fetchStats();
        setPagination(prev => ({ ...prev, current: 1 }));
    }, [productCode, dateRange]);

    const handleTableChange = (pag, _, sorter) => {
        if (sorter?.field) {
            setSortBy(sorter.field);
            setSortDir(sorter.order === 'ascend' ? 'ASC' : 'DESC');
        }
        fetchTransactions(pag.current - 1, pag.pageSize);
        setPagination(pag);
    };

    const handleApplyFilters = () => {
        fetchTransactions(0, pagination.pageSize);
        fetchStats();
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleClearFilters = () => {
        setDateRange(null);
        setStatusFilter(null);
        setReferenceSearch('');
        setTimeout(() => {
            fetchTransactions(0, pagination.pageSize);
            fetchStats();
        }, 100);
    };

    /* ── Export ── */
    const handleExportPDF = () => { message.loading({ content: 'Generating PDF…', key: 'pdf', duration: 1.5 }); setTimeout(() => message.success({ content: 'PDF exported!', key: 'pdf' }), 1600); };
    const handleExportExcel = () => { message.loading({ content: 'Generating Excel…', key: 'excel', duration: 1.5 }); setTimeout(() => message.success({ content: 'Excel exported!', key: 'excel' }), 1600); };
    const handlePrint = () => window.print();
    const handleShare = () => { navigator.clipboard?.writeText(window.location.href); message.success('Link copied!'); };

    /* ── Stats ── */
    const totalCount = stats?.totalTransactions ?? transactions.length;
    const totalVolume = stats?.totalAmount ?? transactions.reduce((s, t) => s + (t.amount || 0), 0);
    const successCount = stats?.successCount ?? transactions.filter(t => ['SUCCESS', 'SUCCESSFUL'].includes((t.status || '').toUpperCase())).length;
    const failedCount = stats?.failedCount ?? transactions.filter(t => t.status?.toUpperCase() === 'FAILED').length;

    /* ── Client-side filter ── */
    const filteredData = transactions.filter(t => {
        const matchRef = referenceSearch
            ? (t.reference || t.transactionReference || t.ref || '').toLowerCase().includes(referenceSearch.toLowerCase())
            : true;
        const matchStatus = statusFilter
            ? (t.status || '').toUpperCase() === statusFilter.toUpperCase()
            : true;
        return matchRef && matchStatus;
    });

    /* ── Columns ── */
    const columns = [
        {
            title: 'Reference',
            key: 'reference',
            width: 180,
            render: (_, r) => {
                const ref = r.reference || r.transactionReference || r.ref || '—';
                return (
                    <Space>
                        <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#ff6700', fontSize: 12 }}>
                            {ref.length > 18 ? `...${ref.slice(-14)}` : ref}
                        </span>
                        {ref !== '—' && (
                            <Tooltip title="Copy">
                                <CopyOutlined style={{ color: '#bbb', cursor: 'pointer', fontSize: 11 }}
                                    onClick={() => navigator.clipboard?.writeText(ref).then(() => message.success('Copied!'))} />
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Merchant',
            key: 'merchantId',
            width: 160,
            render: (_, r) => {
                const mid = r.merchantId || r.clientId;
                const mname = r.merchantName || r.companyName || mid;
                return mid ? (
                    <button
                        className="merchant-link-btn"
                        onClick={() => navigate(`/transactions_history/${productCode}/${mid}`, {
                            state: { merchantName: mname, productName, productCode }
                        })}
                    >
                        {mname}
                    </button>
                ) : <span style={{ color: '#bbb' }}>—</span>;
            },
        },
        {
            title: 'Narration',
            key: 'narration',
            ellipsis: true,
            render: (_, r) => <span style={{ color: '#595959', fontSize: 13 }}>{r.narration || r.description || '—'}</span>,
        },
        {
            title: 'Amount',
            key: 'amount',
            align: 'right',
            width: 140,
            render: (_, r) => <span style={{ fontWeight: 700, fontSize: 14 }}>{formatAmount(r.amount)}</span>,
            sorter: true,
        },
        {
            title: 'Fee',
            key: 'fee',
            align: 'right',
            width: 110,
            render: (_, r) => <span style={{ color: '#8c8c8c', fontSize: 12 }}>{formatAmount(r.fee || r.transactionFee || 0)}</span>,
        },
        {
            title: 'Channel',
            key: 'channel',
            width: 110,
            render: (_, r) => <Tag style={{ borderRadius: 6, fontSize: 11 }}>{r.channel || r.paymentChannel || '—'}</Tag>,
        },
        {
            title: 'Date',
            key: 'transactionDate',
            width: 155,
            render: (_, r) => {
                const d = r.transactionDate || r.createdAt || r.date;
                return <span style={{ color: '#595959', fontSize: 12 }}>{d ? dayjs(d).format('DD MMM YY, HH:mm') : '—'}</span>;
            },
            sorter: true,
            defaultSortOrder: 'descend',
        },
        {
            title: 'Status',
            key: 'status',
            width: 120,
            render: (_, r) => <StatusTag status={r.status} />,
        },
    ];

    return (
        <div className="pt-wrapper">
            {/* ── Breadcrumb ── */}
            <PageBreadcrumb
                items={[
                    { label: 'B2B Products', path: '/b2b-products' },
                    { label: productName },
                    { label: 'Transactions' },
                ]}
            />

            {/* ── Page Header ── */}
            <div className="pt-header">
                <div className="pt-header-left">
                    <div className="pt-header-icon"><SwapOutlined /></div>
                    <div>
                        <h2 className="pt-header-title">Product Transactions</h2>
                        <p className="pt-header-subtitle">{productName} &middot; {productCode}</p>
                    </div>
                </div>
                <Space wrap size={8}>
                    <Tooltip title="Refresh">
                        <Button icon={<ReloadOutlined />} onClick={() => { fetchTransactions(0, pagination.pageSize); fetchStats(); }} loading={loading} className="pt-export-btn" />
                    </Tooltip>
                    <Tooltip title="Export to PDF"><Button icon={<FilePdfOutlined />} onClick={handleExportPDF} className="pt-export-btn pt-btn-pdf">PDF</Button></Tooltip>
                    <Tooltip title="Export to Excel"><Button icon={<FileExcelOutlined />} onClick={handleExportExcel} className="pt-export-btn pt-btn-excel">Excel</Button></Tooltip>
                    <Tooltip title="Print"><Button icon={<PrinterOutlined />} onClick={handlePrint} className="pt-export-btn pt-btn-print">Print</Button></Tooltip>
                    <Tooltip title="Share link"><Button icon={<ShareAltOutlined />} onClick={handleShare} className="pt-export-btn pt-btn-share">Share</Button></Tooltip>
                </Space>
            </div>

            {/* ── Stats Row ── */}
            <Row gutter={[16, 16]} className="pt-stats-row">
                <Col xs={12} sm={12} md={6}>
                    <Spin spinning={statsLoading} size="small">
                        <div className="pt-stat-card">
                            <SwapOutlined className="pt-stat-icon" style={{ color: '#ff6700' }} />
                            <div>
                                <div className="pt-stat-value">{totalCount.toLocaleString()}</div>
                                <div className="pt-stat-label">Total Transactions</div>
                            </div>
                        </div>
                    </Spin>
                </Col>
                <Col xs={12} sm={12} md={6}>
                    <Spin spinning={statsLoading} size="small">
                        <div className="pt-stat-card">
                            <DollarCircleOutlined className="pt-stat-icon" style={{ color: '#0f9b58' }} />
                            <div>
                                <div className="pt-stat-value" style={{ fontSize: 16 }}>{formatAmount(totalVolume)}</div>
                                <div className="pt-stat-label">Total Volume</div>
                            </div>
                        </div>
                    </Spin>
                </Col>
                <Col xs={12} sm={12} md={6}>
                    <div className="pt-stat-card">
                        <CheckCircleOutlined className="pt-stat-icon" style={{ color: '#52c41a' }} />
                        <div>
                            <div className="pt-stat-value" style={{ color: '#52c41a' }}>{successCount.toLocaleString()}</div>
                            <div className="pt-stat-label">Successful</div>
                        </div>
                    </div>
                </Col>
                <Col xs={12} sm={12} md={6}>
                    <div className="pt-stat-card">
                        <CloseCircleOutlined className="pt-stat-icon" style={{ color: '#ff4d4f' }} />
                        <div>
                            <div className="pt-stat-value" style={{ color: '#ff4d4f' }}>{failedCount.toLocaleString()}</div>
                            <div className="pt-stat-label">Failed</div>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* ── Filter Bar ── */}
            <div className="pt-filter-bar">
                <div className="pt-filter-title">
                    <FilterOutlined style={{ marginRight: 6 }} />
                    Filters
                </div>
                <div className="pt-filters">
                    <div className="pt-filter-item">
                        <label className="pt-filter-label">Reference</label>
                        <Input
                            prefix={<SearchOutlined style={{ color: '#bbb' }} />}
                            placeholder="Search reference…"
                            value={referenceSearch}
                            onChange={e => setReferenceSearch(e.target.value)}
                            style={{ width: 200, borderRadius: 8 }}
                            allowClear
                        />
                    </div>
                    <div className="pt-filter-item">
                        <label className="pt-filter-label">Date Range</label>
                        <RangePicker
                            value={dateRange}
                            onChange={range => setDateRange(range)}
                            style={{ borderRadius: 8 }}
                            format="DD MMM YYYY"
                            placeholder={['Start date', 'End date']}
                            disabledDate={d => d && d.isAfter(dayjs())}
                        />
                    </div>
                    <div className="pt-filter-item">
                        <label className="pt-filter-label">Status</label>
                        <Select
                            allowClear
                            placeholder="All statuses"
                            value={statusFilter}
                            onChange={val => setStatusFilter(val)}
                            style={{ width: 160, borderRadius: 8 }}
                        >
                            {['SUCCESS', 'PENDING', 'FAILED', 'REVERSED'].map(s => (
                                <Option key={s} value={s}>{s}</Option>
                            ))}
                        </Select>
                    </div>
                    <Space>
                        <Button type="primary" onClick={handleApplyFilters} style={{ background: '#ff6700', borderColor: '#ff6700', borderRadius: 8 }}>
                            Apply
                        </Button>
                        <Button onClick={handleClearFilters} className="pt-clear-btn">Clear</Button>
                    </Space>
                </div>
            </div>

            {/* ── Table ── */}
            <div className="pt-table-card">
                <div className="pt-table-header-row">
                    <span className="pt-table-count">
                        Showing <strong>{filteredData.length}</strong> transactions for <strong>{productName}</strong>
                    </span>
                </div>
                <Table
                    columns={columns}
                    dataSource={filteredData}
                    rowKey={r => r.id || r.reference || r.transactionReference || r.ref || Math.random().toString()}
                    scroll={{ x: 980 }}
                    loading={loading}
                    onChange={handleTableChange}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`,
                    }}
                    rowClassName={(_, idx) => (idx % 2 === 0 ? 'pt-row-even' : 'pt-row-odd')}
                    className="pt-table"
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={loading ? 'Loading transactions…' : 'No transactions found for this product'}
                            />
                        ),
                    }}
                />
            </div>
        </div>
    );
};

export default ProductTransactions;
